<template>
	<!-- The Topbar component -->
	<div>
		<ul class="list-unstyled topbar-right-menu float-right mb-0">
			<!-- Main search input form for mobile (only for Deliveries page): -->
			<b-nav-item-dropdown
				v-if="$route.name === 'Deliveries'"
				id="item-primary-search-mobile"
				class="notification-list topbar-dropdown d-md-none"
				toggle-class="arrow-none"
				menu-class="dropdown-menu-animated topbar-dropdown-menu"
			>
				<template #button-content>
					<i class="mdi mdi-magnify noti-icon" />
				</template>

				<div class="p-0">
					<form class="p-3" @submit.prevent="submitSearch">
						<div class="input-group input-group-mobile">
							<input
								id="input-primary-search-mobile"
								v-model="searchString"
								type="text"
								class="form-control input-primary"
								:placeholder="
									$t(
										'PORTAL.Home.placeholder_search_tracking_id'
									)
								"
							/>
							<div class="input-group-append">
								<button class="btn btn-primary" type="submit">
									<i class="mdi mdi-magnify" />
									{{ $t('PORTAL.Home.button_search') }}
								</button>
							</div>
						</div>
					</form>
				</div>
			</b-nav-item-dropdown>

			<!-- For testing layout: -->
			<!-- <li v-if="enableLayoutSetting" class="notification-list">
				<a
					class="nav-link right-bar-toggle toggle-right cursor-pointer"
					@click="toggleRightSidebar"
				>
					<i class="mdi mdi-settings-outline noti-icon toggle-right" />
				</a>
			</li> -->

			<b-nav-item class="notification-list" @click="onDashboardToggle">
				<i
					v-b-popover.hover.left="
						$t('PORTAL.delivery_overview.title')
					"
					v-b-toggle.collapse-2
					:class="`mdi mdi-chart-bar noti-icon ${
						showDashboard ? 'dashboard-active' : ''
					}`"
				/>
			</b-nav-item>

			<!-- For testing i18n: -->
			<b-nav-item-dropdown
				class="notification-list topbar-dropdown"
				right
				toggle-class="arrow-none"
				menu-class="dropdown-menu-animated topbar-dropdown-menu"
			>
				<template #button-content>
					<i
						v-b-popover.hover.left="
							$t('PORTAL.Home.popover_change_language')
						"
						class="mdi mdi-translate noti-icon"
					/>
				</template>

				<div class="notify-item">
					<b-form-radio-group
						v-model="config.locale"
						stacked
						@change="changeLanguage($event)"
					>
						<b-form-radio
							v-for="locale in config.locales"
							:key="locale.id"
							:value="locale.id"
							class="mb-1"
						>
							{{ locale.translations[0].value }} ({{
								locale.translations[0].locale.split('-')[1]
							}})
						</b-form-radio>
					</b-form-radio-group>
				</div>
			</b-nav-item-dropdown>

			<b-nav-item-dropdown
				v-if="user"
				class="notification-list"
				right
				toggle-class="nav-user arrow-none mr-0"
				menu-class="dropdown-menu-animated topbar-dropdown-menu profile-dropdown"
			>
				<template #button-content>
					<span class="account-user-avatar">
						<img
							:src="user.photo || defaultAvatar"
							alt="user-image"
							class="rounded-circle"
						/>
					</span>
					<span>
						<span class="account-user-name">{{ user.name }}</span>
						<span class="account-position">
							{{
								user.roles
									.map(
										role =>
											$t(`PORTAL.ROLES.${role}`) || role
									)
									.join(', ')
							}}
						</span>
					</span>
				</template>

				<div class="dropdown-header noti-title">
					<h6 v-if="user.clientId" class="text-overflow m-0">
						{{ user.clientId }}
					</h6>
					<h6 class="text-overflow m-0">{{ user.email }}</h6>
				</div>

				<router-link
					tag="a"
					class="dropdown-item notify-item text-danger"
					to="/logout"
				>
					<i class="mdi mdi-logout mr-1" />
					<span>{{ $t('Common.button_logout') }}</span>
				</router-link>
			</b-nav-item-dropdown>
			<li v-else class="notification-list">
				<router-link tag="a" class="nav-link" to="/login">
					<i class="mdi mdi-login noti-icon" />
					<span>{{ $t('PORTAL.Home.button_login') }}</span>
				</router-link>
			</li>
		</ul>
		<button
			v-if="layout !== 'detached'"
			class="button-menu-mobile open-left disable-btn toggle-menu"
			@click="toggleMenu"
		>
			<i class="mdi mdi-menu toggle-menu" />
		</button>

		<a
			v-else
			href="javascript: void(0)"
			class="button-menu-mobile open-left disable-btn toggle-menu"
			@click="toggleMenu"
		>
			<div class="lines toggle-menu">
				<span />
				<span />
				<span />
			</div>
		</a>

		<!-- Main search input form (only for Deliveries page): -->
		<div
			v-if="$route.name === 'Deliveries'"
			class="app-search d-none d-md-block d-lg-block"
		>
			<form @submit.prevent="submitSearch">
				<div class="input-group">
					<input
						id="input-primary-search"
						v-model="searchString"
						type="text"
						class="form-control input-primary"
						:placeholder="
							$t('PORTAL.Home.placeholder_search_tracking_id')
						"
					/>
					<span class="mdi mdi-magnify" />
					<div class="input-group-append">
						<button class="btn btn-primary" type="submit">
							{{ $t('PORTAL.Home.button_search') }}
						</button>
					</div>
				</div>
			</form>
		</div>
	</div>
</template>

<script>
import {
	layoutComputed,
	configMethods,
	deliveriesMethods,
	dashboardComputed,
	dashboardMethods
} from '@/store/helpers'
import usersAvatar from '@/assets/images/users-avatar.svg'

export default {
	name: 'Topbar',
	props: {
		user: {
			type: Object,
			required: false,
			default: () => null
		},
		layout: {
			type: String,
			default: ''
		}
	},
	data() {
		const { VUE_APP_ENABLE_LAYOUT_SETTING } = process.env
		return {
			config: this.$store ? this.$store.state.config : {} || {},
			searchString: '',
			defaultAvatar: usersAvatar,
			enableLayoutSetting: VUE_APP_ENABLE_LAYOUT_SETTING === 'true'
		}
	},
	computed: {
		...layoutComputed,
		...dashboardComputed
	},
	watch: {
		'$route.query.search': function (clientRef) {
			if (!clientRef && this.searchString) {
				this.searchString = ''
				this.clearDeliveries()
			} else if (clientRef && clientRef !== this.searchString) {
				this.searchString = clientRef
				this.submitSearch()
			}
		}
	},
	mounted() {
		const { clientRef } = this.$route.params
		const { search } = this.$route.query
		if (search || clientRef) {
			// search query parameter has priority:
			this.searchString = search || clientRef
			this.submitSearch()
		}
		this.fetchLocales()
	},
	methods: {
		...configMethods,
		...deliveriesMethods,
		...dashboardMethods,
		toggleMenu() {
			this.$parent.toggleMenu()
		},
		toggleRightSidebar() {
			this.$parent.toggleRightSidebar()
		},
		async submitSearch() {
			if (this.searchString) {
				await this.fetchDeliveries({ clientRef: this.searchString })
			} else {
				this.clearDeliveries()
			}
		},
		changeLanguage(newLocale) {
			this.changeLocale({ newLocale })
		},
		onDashboardToggle() {
			this.toggleDashboard()
			if (this.showDashboard) {
				this.fetchOverview()
			}
		}
	}
}
</script>

<style scoped>
input.input-primary {
	border: 1px solid #ced4da;
}
input.input-primary:focus {
	border: 1px solid #f16622;
	caret-color: #f16622;
}
.input-group-mobile {
	min-width: 300px;
}
.dashboard-active {
	color: #6c757d;
}
</style>
